import { Delete, Edit, ExpandMore, Save } from '@mui/icons-material';
// Components
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormLabel,
  IconButton,
  TextField,
  Typography
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import React, { FC, useState } from 'react';
import { formatMoney } from '../../../helpers';
// Types
import { ISpecOption } from '../../../models';

interface IMobileCategoryOptions {
  original: ISpecOption;
  handleEdit?: (val: ISpecOption) => void;
  handleDelete?: (val: ISpecOption) => void;
  editingId?: string;
  setOptions: any;
  options: ISpecOption[];
}

export const MobileCategoryOptions: FC<IMobileCategoryOptions> = ({
  original,
  handleEdit,
  handleDelete,
  editingId,
  options,
  setOptions
}) => {
  const classes = useStyles();
  const { category, name, price, quantity, optionId } = original;
  const [editName, setEditName] = useState(name);
  const [editPrice, setEditPrice] = useState(price);
  const [editQuantity, setEditQuantity] = useState(quantity);

  const isEditing = editingId === optionId;
  return (
    <Accordion className={classes.root}>
      <AccordionSummary
        className={classes.accordion}
        expandIcon={<ExpandMore />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <div className={classes.topPanelSummaryWrapper}>
          <Typography variant="body2">{name}</Typography>
          <div className={classes.buttonsWrapper}>
            {handleEdit && (
              <IconButton
                className={classes.editButton}
                color="primary"
                onClick={e => {
                  if (isEditing) {
                    setOptions(
                      options.map(option => {
                        if (option.optionId === editingId) {
                          return {
                            ...option,
                            name: editName,
                            price: editPrice,
                            quantity: editQuantity
                          };
                        }
                        return option;
                      })
                    );
                  }
                  e.stopPropagation();
                  handleEdit(original);
                }}
                size="large"
              >
                {isEditing ? <Save /> : <Edit />}
              </IconButton>
            )}
            {handleDelete && (
              <IconButton
                className={classes.editButton}
                color="error"
                onClick={e => {
                  e.stopPropagation();
                  handleDelete(original);
                }}
                size="large"
              >
                <Delete />
              </IconButton>
            )}
          </div>
        </div>
      </AccordionSummary>
      <AccordionDetails className={classes.details}>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Class:
          </FormLabel>
          <Typography component="span">{category || '--'}</Typography>
        </div>
        <div className={isEditing ? classes.panelSummaryWrapperEdit : classes.panelSummaryWrapper}>
          {isEditing ? (
            <TextField
              fullWidth
              required
              variant="outlined"
              autoComplete="nope"
              label="Name"
              name="editName"
              value={editName}
              size="small"
              onChange={e => setEditName(e.target.value)}
            />
          ) : (
            <>
              <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
                Name:
              </FormLabel>
              <Typography component="span" variant="body2">
                {name || '--'}
              </Typography>
            </>
          )}
        </div>
        <div className={isEditing ? classes.panelSummaryWrapperEdit : classes.panelSummaryWrapper}>
          {isEditing ? (
            <TextField
              fullWidth
              required
              variant="outlined"
              autoComplete="nope"
              label="Price"
              name="editPrice"
              value={editPrice}
              size="small"
              onChange={e => setEditPrice(Number(e.target.value))}
            />
          ) : (
            <>
              <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
                Price:
              </FormLabel>
              <Typography component="span" variant="body2">
                {formatMoney(price) || '--'}
              </Typography>
            </>
          )}
        </div>
        <div className={isEditing ? classes.panelSummaryWrapperEdit : classes.panelSummaryWrapper}>
          {isEditing ? (
            <TextField
              fullWidth
              required
              variant="outlined"
              autoComplete="nope"
              label="Quantity"
              name="editQuantity"
              value={editQuantity}
              size="small"
              onChange={e => setEditQuantity(Number(e.target.value))}
            />
          ) : (
            <>
              <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
                Quantity:
              </FormLabel>
              <Typography component="span" variant="body2">
                {formatMoney(quantity) || '--'}
              </Typography>
            </>
          )}
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    overflowX: 'hidden'
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3
    }
  },
  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    '&:first-child': { marginTop: 0 }
  },
  panelSummaryWrapper: {
    '&:first-child': { marginTop: 0 }
  },
  panelSummaryWrapperEdit: {
    marginTop: theme.spacing(1)
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 16px',
    backgroundColor: theme.palette.grey[100]
  },
  editButton: {
    padding: `2px 5px`
  },
  boldName: {
    color: theme.palette.typography.labels,
    fontWeight: 600
  },
  subLabel: {
    marginRight: 10,
    fontSize: theme.typography.body2.fontSize
  },
  buttonsWrapper: {
    display: 'flex'
  }
}));
